import { useState, useEffect } from 'react'
import { updateProfile } from 'firebase/auth'
import { useAuth } from '../../contexts/AuthContext'
import { customerService, subscriptionService } from '../../services/firebase'
import { auth } from '../../firebase'
import config from '../../config/environment'
import { washPackages } from '@/config/washCatalog'
import WashPriceListSection from '@/components/WashPriceListSection'
import { voucherProgramService } from '@/services/voucherProgramService'
import { voucherService } from '@/services/voucherService'
import { formatRand, getVoucherLoyalty } from '@/utils/voucherLoyalty'
import {
  User,
  Phone,
  Mail,
  Calendar,
  Star,
  Car,
  Edit,
  Save,
  X,
  LogOut,
  ClipboardList,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import toast from 'react-hot-toast'
import BottomNav from '../../components/BottomNav'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Badge } from '@/components/ui/badge'

function getDateFromTimestamp(timestamp) {
  if (!timestamp) return new Date()
  if (timestamp.toDate && typeof timestamp.toDate === 'function') {
    return timestamp.toDate()
  }
  return new Date(timestamp)
}

const packageNames = washPackages.map((p) => p.name)

function isPlaceholderPhone(phone) {
  const p = String(phone || '').replace(/\s/g, '')
  return p === '+27821234567' || p === '0821234567'
}

function vehicleFromCustomer(c) {
  const v = c?.primaryVehicle || {}
  return {
    make: String(v.make || v.brand || '').trim(),
    type: String(v.type || '').trim(),
    model: String(v.model || '').trim(),
    color: String(v.color || v.colour || '').trim(),
    licensePlate: String(v.licensePlate || v.registration || v.reg || '').trim(),
  }
}

export default function CustomerProfile() {
  const { currentUser, signOut } = useAuth()
  const [customer, setCustomer] = useState(null)
  const [subscription, setSubscription] = useState(null)
  const [loading, setLoading] = useState(true)
  const [editing, setEditing] = useState(false)
  const [needsOnboarding, setNeedsOnboarding] = useState(false)
  const [onboardingCustomerId, setOnboardingCustomerId] = useState(null)
  const [voucherProgram, setVoucherProgram] = useState(null)
  const [vouchers, setVouchers] = useState([])
  const [voucherSummary, setVoucherSummary] = useState({ spendProgress: 0, balance: 0 })
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    preferredServices: [],
    vehicleBrand: '',
    vehicleType: '',
    vehicleModel: '',
    vehicleColour: '',
    vehicleReg: '',
  })
  const [onboarding, setOnboarding] = useState({
    name: '',
    phone: '',
    vehicleBrand: '',
    vehicleType: '',
    vehicleModel: '',
    vehicleColour: '',
    vehicleReg: '',
  })

  useEffect(() => {
    async function fetchCustomerData() {
      if (!currentUser) {
        setLoading(false)
        return
      }

      try {
        // Ensure a customer-owned profile exists so login never hard-fails.
        await customerService.ensureCustomerProfileForUser(currentUser)
        const customerData = await customerService.getCustomerByUserId(currentUser.uid)
        setCustomer(customerData)

        const vehicle = vehicleFromCustomer(customerData)
        const fromAuthName = String(currentUser?.displayName || '').trim()
        const name = String(customerData?.name || '').trim() || fromAuthName
        const phoneRaw = String(customerData?.phone || '').trim()
        const phone = isPlaceholderPhone(phoneRaw) ? '' : phoneRaw

        const emailMismatch =
          !!customerData?.email &&
          !!currentUser?.email &&
          String(customerData.email).toLowerCase() !== String(currentUser.email).toLowerCase()

        const missingClientDetails = !name || !phone || emailMismatch
        const needsVehicleStep = customerData?.isSystemProfile === true

        if (missingClientDetails || needsVehicleStep) {
          // Name/phone already captured at registration — only collect what's missing, then vehicle.
          setNeedsOnboarding(true)
          setOnboardingCustomerId(customerData?.id || null)
          setOnboarding({
            name,
            phone,
            vehicleBrand: vehicle.make,
            vehicleType: vehicle.type,
            vehicleModel: vehicle.model,
            vehicleColour: vehicle.color,
            vehicleReg: vehicle.licensePlate,
          })
          setLoading(false)
          return
        } else {
          setFormData({
            name: name || currentUser.displayName || '',
            phone: phone || '',
            email: customerData.email || currentUser.email || '',
            address: customerData.address || '',
            preferredServices: customerData.preferredServices || [],
            vehicleBrand: vehicle.make,
            vehicleType: vehicle.type,
            vehicleModel: vehicle.model,
            vehicleColour: vehicle.color,
            vehicleReg: vehicle.licensePlate,
          })
        }

        // Voucher program + vouchers list
        const ids = await customerService.listCustomerIdsForUser(currentUser)
        const profiles = await customerService.getCustomersByIds(ids)
        const spendProgress = profiles.reduce((sum, c) => sum + Number(c?.voucherSpendProgress || 0), 0)
        const balance = profiles.reduce((sum, c) => sum + Number(c?.voucherBalance || 0), 0)
        setVoucherSummary({ spendProgress, balance })

        const [program, list, sub] = await Promise.all([
          voucherProgramService.get(),
          voucherService.listCustomerVouchersByCustomerIds(ids, 30),
          subscriptionService.getCustomerSubscriptionByCustomerIds(ids),
        ])
        setVoucherProgram(program)
        setVouchers(list)
        setSubscription(sub)
      } catch (error) {
        console.error('Error fetching customer data:', error)
        toast.error('Failed to load profile data')
      } finally {
        setLoading(false)
      }
    }

    fetchCustomerData()
  }, [currentUser])

  const handleOnboardingSubmit = async (e) => {
    e.preventDefault()
    if (!currentUser) return
    if (!onboarding.name.trim() || !onboarding.phone.trim()) {
      toast.error('Please enter your name and mobile number')
      return
    }

    try {
      setLoading(true)

      const primaryVehicle = {
        make: onboarding.vehicleBrand.trim() || null,
        type: onboarding.vehicleType.trim() || null,
        model: onboarding.vehicleModel.trim() || null,
        color: onboarding.vehicleColour.trim() || null,
        licensePlate: onboarding.vehicleReg.trim() || null,
      }
      const hasVehicle = Object.values(primaryVehicle).some((v) => v)

      const payload = {
        userId: currentUser.uid,
        name: onboarding.name.trim(),
        email: currentUser.email || '',
        phone: onboarding.phone.trim(),
        address: '',
        loyaltyPoints: 0,
        voucherBalance: 0,
        totalWashes: 0,
        washSelectionLog: [],
        createdAt: new Date(),
        primaryVehicle: hasVehicle ? primaryVehicle : null,
      }

      let id = onboardingCustomerId
      if (id) {
        await customerService.updateCustomer(id, {
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          primaryVehicle: payload.primaryVehicle,
          isSystemProfile: false,
          updatedAt: new Date(),
        })
        setCustomer((prev) => ({ ...(prev || {}), ...payload, id, isSystemProfile: false }))
      } else {
        id = await customerService.createCustomer(payload)
        setCustomer({ ...payload, id })
      }
      setNeedsOnboarding(false)
      setOnboardingCustomerId(null)
      setFormData({
        name: payload.name,
        phone: payload.phone,
        email: payload.email,
        address: payload.address,
        preferredServices: [],
        vehicleBrand: primaryVehicle.make || '',
        vehicleType: primaryVehicle.type || '',
        vehicleModel: primaryVehicle.model || '',
        vehicleColour: primaryVehicle.color || '',
        vehicleReg: primaryVehicle.licensePlate || '',
      })

      if (!config.devMode && auth.currentUser && payload.name) {
        await updateProfile(auth.currentUser, { displayName: payload.name })
      }

      toast.success('Welcome to TrendSetter')
    } catch (error) {
      console.error(error)
      toast.error('Could not create your profile')
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    try {
      setLoading(true)

      const vehicle = {
        make: String(formData.vehicleBrand || '').trim() || null,
        type: String(formData.vehicleType || '').trim() || null,
        model: String(formData.vehicleModel || '').trim() || null,
        color: String(formData.vehicleColour || '').trim() || null,
        licensePlate: String(formData.vehicleReg || '').trim() || null,
      }
      const hasVehicle = Object.values(vehicle).some((v) => v)

      if (customer?.id && customer.id !== 'pending') {
        await customerService.updateCustomer(customer.id, {
          name: formData.name,
          phone: formData.phone,
          email: formData.email,
          address: formData.address,
          preferredServices: formData.preferredServices,
          primaryVehicle: hasVehicle ? vehicle : null,
          isSystemProfile: false,
        })
      }

      if (
        !config.devMode &&
        currentUser &&
        formData.name &&
        formData.name !== currentUser.displayName
      ) {
        await updateProfile(auth.currentUser, { displayName: formData.name })
      }

      setCustomer({
        ...customer,
        ...formData,
        primaryVehicle: hasVehicle ? vehicle : null,
      })

      setEditing(false)
      toast.success('Profile updated successfully')
    } catch (error) {
      console.error('Error updating profile:', error)
      toast.error('Failed to update profile')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    const vehicle = vehicleFromCustomer(customer)
    setFormData({
      name: customer?.name || currentUser?.displayName || '',
      phone: customer?.phone || '',
      email: customer?.email || currentUser?.email || '',
      address: customer?.address || '',
      preferredServices: customer?.preferredServices || [],
      vehicleBrand: vehicle.make,
      vehicleType: vehicle.type,
      vehicleModel: vehicle.model,
      vehicleColour: vehicle.color,
      vehicleReg: vehicle.licensePlate,
    })
    setEditing(false)
  }

  const togglePreferredService = (service) => {
    setFormData((prev) => ({
      ...prev,
      preferredServices: prev.preferredServices.includes(service)
        ? prev.preferredServices.filter((s) => s !== service)
        : [...prev.preferredServices, service],
    }))
  }

  const handleLogout = async () => {
    try {
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Failed to logout')
    }
  }

  if (loading && !customer && !formData.name) {
    return (
      <div className="flex justify-center items-center h-64 bg-background">
        <LoadingSpinner />
      </div>
    )
  }

  if (needsOnboarding) {
    const clientDetailsKnown =
      Boolean(String(onboarding.name || '').trim()) &&
      Boolean(String(onboarding.phone || '').trim()) &&
      !isPlaceholderPhone(onboarding.phone)

    return (
      <>
        <div className="min-h-screen bg-background pb-28">
          <div className="mx-auto max-w-lg px-4 py-8 space-y-6">
            <div className="text-center space-y-2">
              <img
                src="/brand/trendsetter-logo.png"
                alt="TrendSetter"
                className="h-12 w-auto object-contain mx-auto"
              />
              <h1 className="text-2xl font-semibold tracking-tight text-foreground">
                {clientDetailsKnown ? 'Your vehicle' : 'Welcome'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {clientDetailsKnown
                  ? 'Add the car you usually bring in. Staff will use this at check-in.'
                  : 'Please complete your details. Vehicle info is optional.'}
              </p>
            </div>

            <Card className="border-border/80 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">
                  {clientDetailsKnown ? 'Vehicle details' : 'Quick onboarding'}
                </CardTitle>
                <CardDescription>
                  {clientDetailsKnown
                    ? 'Optional — you can skip and add this later on your profile.'
                    : 'Name and mobile number are required.'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleOnboardingSubmit} className="space-y-4">
                  {!clientDetailsKnown && (
                    <>
                      <div className="space-y-2">
                        <Label>Name *</Label>
                        <Input
                          value={onboarding.name}
                          onChange={(e) =>
                            setOnboarding((p) => ({ ...p, name: e.target.value }))
                          }
                          placeholder="Your name"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Mobile number *</Label>
                        <Input
                          value={onboarding.phone}
                          onChange={(e) =>
                            setOnboarding((p) => ({ ...p, phone: e.target.value }))
                          }
                          placeholder="+27..."
                          inputMode="tel"
                        />
                      </div>
                      <Separator />
                    </>
                  )}

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label>Vehicle brand</Label>
                      <Input
                        value={onboarding.vehicleBrand}
                        onChange={(e) =>
                          setOnboarding((p) => ({
                            ...p,
                            vehicleBrand: e.target.value,
                          }))
                        }
                        placeholder="e.g. Toyota"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Vehicle type</Label>
                      <Input
                        value={onboarding.vehicleType}
                        onChange={(e) =>
                          setOnboarding((p) => ({
                            ...p,
                            vehicleType: e.target.value,
                          }))
                        }
                        placeholder="e.g. SUV"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Model</Label>
                      <Input
                        value={onboarding.vehicleModel}
                        onChange={(e) =>
                          setOnboarding((p) => ({
                            ...p,
                            vehicleModel: e.target.value,
                          }))
                        }
                        placeholder="e.g. Corolla"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Colour</Label>
                      <Input
                        value={onboarding.vehicleColour}
                        onChange={(e) =>
                          setOnboarding((p) => ({
                            ...p,
                            vehicleColour: e.target.value,
                          }))
                        }
                        placeholder="e.g. White"
                      />
                    </div>
                    <div className="space-y-2 sm:col-span-2">
                      <Label>Registration</Label>
                      <Input
                        value={onboarding.vehicleReg}
                        onChange={(e) =>
                          setOnboarding((p) => ({
                            ...p,
                            vehicleReg: e.target.value,
                          }))
                        }
                        placeholder="Optional"
                      />
                    </div>
                  </div>

                  <Button type="submit" className="w-full" disabled={loading}>
                    Continue
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        <BottomNav />
      </>
    )
  }

  const log = [...(customer?.washSelectionLog || [])].reverse()
  const loyalty = getVoucherLoyalty(
    {
      voucherSpendProgress: voucherSummary.spendProgress,
      voucherBalance: voucherSummary.balance,
    },
    voucherProgram
  )
  const threshold = loyalty.threshold
  const progressPct = loyalty.pct

  return (
    <div className="min-h-screen bg-background pb-28">
      <div className="mx-auto max-w-4xl px-4 py-8 space-y-8">
        <div className="flex flex-col items-center text-center gap-3">
          <img
            src="/brand/trendsetter-logo.png"
            alt="TrendSetter"
            className="h-12 w-auto object-contain"
          />
          <h1 className="text-2xl font-semibold tracking-tight text-foreground">
            My profile
          </h1>
          <p className="text-sm text-muted-foreground max-w-md">
            Update your details, review wash prices, and see what was selected at your last
            visits.
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          <Card className="md:col-span-1">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <User className="size-4" />
                Summary
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-sm">
              <div>
                <p className="font-medium text-foreground">
                  {customer?.name || currentUser?.displayName || 'Customer'}
                </p>
                <p className="text-muted-foreground break-all">
                  {customer?.email || currentUser?.email}
                </p>
              </div>
              <Separator />
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Star className="size-3.5" /> Towards next voucher
                </span>
                <span className="font-medium tabular-nums">
                  {loyalty.progressLabel}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  Voucher balance
                </span>
                <span className="font-medium tabular-nums">
                  {formatRand(loyalty.balance)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Car className="size-3.5" /> Washes
                </span>
                <span className="font-medium tabular-nums">
                  {customer?.totalWashes ?? 0}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground flex items-center gap-1">
                  <Calendar className="size-3.5" /> Member
                </span>
                <span className="text-muted-foreground text-xs">
                  {customer?.createdAt
                    ? getDateFromTimestamp(customer.createdAt).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleLogout}
              >
                <LogOut className="size-4" />
                Sign out
              </Button>
            </CardContent>
          </Card>

          <Card className="md:col-span-2">
            <CardHeader className="flex flex-row items-start justify-between gap-4 pb-2">
              <div>
                <CardTitle className="text-base">Contact & vehicle</CardTitle>
                <CardDescription>Used for bookings, receipts, and check-in.</CardDescription>
              </div>
              {!editing ? (
                <Button variant="secondary" size="sm" onClick={() => setEditing(true)}>
                  <Edit className="size-4" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-2">
                  <Button size="sm" onClick={handleSave} disabled={loading}>
                    <Save className="size-4" />
                    Save
                  </Button>
                  <Button size="sm" variant="ghost" onClick={handleCancel}>
                    <X className="size-4" />
                  </Button>
                </div>
              )}
            </CardHeader>
            <CardContent className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2 sm:col-span-2">
                <Label>Name</Label>
                {editing ? (
                  <Input
                    value={formData.name}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, name: e.target.value }))
                    }
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">{formData.name || 'N/A'}</p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Phone className="size-3.5" /> Phone
                </Label>
                {editing ? (
                  <Input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, phone: e.target.value }))
                    }
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.phone || 'N/A'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="flex items-center gap-1">
                  <Mail className="size-3.5" /> Email
                </Label>
                {editing ? (
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, email: e.target.value }))
                    }
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border break-all">
                    {formData.email || 'N/A'}
                  </p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Address</Label>
                {editing ? (
                  <Input
                    value={formData.address}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, address: e.target.value }))
                    }
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.address || 'N/A'}
                  </p>
                )}
              </div>
              <div className="sm:col-span-2 pt-2">
                <p className="text-sm font-medium text-foreground">Vehicle</p>
                <p className="text-xs text-muted-foreground">
                  Used to pre-fill check-in when you arrive.
                </p>
              </div>
              <div className="space-y-2">
                <Label>Brand</Label>
                {editing ? (
                  <Input
                    value={formData.vehicleBrand}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, vehicleBrand: e.target.value }))
                    }
                    placeholder="e.g. Toyota"
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.vehicleBrand || 'N/A'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                {editing ? (
                  <Input
                    value={formData.vehicleType}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, vehicleType: e.target.value }))
                    }
                    placeholder="e.g. SUV"
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.vehicleType || 'N/A'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Model</Label>
                {editing ? (
                  <Input
                    value={formData.vehicleModel}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, vehicleModel: e.target.value }))
                    }
                    placeholder="e.g. Corolla"
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.vehicleModel || 'N/A'}
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label>Colour</Label>
                {editing ? (
                  <Input
                    value={formData.vehicleColour}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, vehicleColour: e.target.value }))
                    }
                    placeholder="e.g. White"
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.vehicleColour || 'N/A'}
                  </p>
                )}
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label>Registration</Label>
                {editing ? (
                  <Input
                    value={formData.vehicleReg}
                    onChange={(e) =>
                      setFormData((p) => ({ ...p, vehicleReg: e.target.value }))
                    }
                    placeholder="Optional"
                  />
                ) : (
                  <p className="text-sm py-2 border-b border-border">
                    {formData.vehicleReg || 'N/A'}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Vouchers</CardTitle>
            <CardDescription>
              Spend progress and voucher history.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-lg border border-border/80 p-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">Progress</span>
                <span className="font-medium tabular-nums">
                  {loyalty.progressLabel}
                </span>
              </div>
              <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted">
                <div className="h-full bg-primary" style={{ width: `${progressPct}%` }} />
              </div>
              <p className="mt-2 text-xs text-muted-foreground">
                When you reach {formatRand(threshold)}, a {formatRand(loyalty.rewardAmount)} voucher is issued automatically.
              </p>
            </div>

            {vouchers.length === 0 ? (
              <p className="text-sm text-muted-foreground">No vouchers yet.</p>
            ) : (
              <div className="space-y-2">
                {vouchers.map((v) => {
                  const issuedAt = v.issuedAt?.toDate ? v.issuedAt.toDate() : v.issuedAt ? new Date(v.issuedAt) : null
                  const redeemedAt = v.redeemedAt?.toDate ? v.redeemedAt.toDate() : v.redeemedAt ? new Date(v.redeemedAt) : null
                  const isRedeemed = v.status === 'redeemed'
                  return (
                    <div
                      key={v.id}
                      className="flex flex-col gap-1 rounded-lg border border-border/80 p-3 sm:flex-row sm:items-center sm:justify-between"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground">
                          {v.rewardLabel || 'Voucher'} · {typeof v.value === 'number' ? `R${v.value.toFixed(2)}` : 'N/A'}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Issued: {issuedAt ? issuedAt.toLocaleDateString('en-ZA') : 'N/A'}
                          {isRedeemed && ` · Redeemed: ${redeemedAt ? redeemedAt.toLocaleDateString('en-ZA') : 'N/A'}`}
                        </p>
                      </div>
                      <Badge variant={isRedeemed ? 'secondary' : 'default'}>
                        {isRedeemed ? 'Redeemed' : 'Active'}
                      </Badge>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Subscription</CardTitle>
            <CardDescription>Your active package and remaining washes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            {!subscription ? (
              <p className="text-muted-foreground">No active subscription.</p>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Package</span>
                  <span className="font-medium">{subscription.planType || 'N/A'}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Expiry</span>
                  <span className="font-medium">
                    {subscription.expiryDate ? getDateFromTimestamp(subscription.expiryDate).toLocaleDateString('en-ZA') : 'N/A'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Used</span>
                  <span className="font-medium tabular-nums">{Number(subscription.washesUsed || 0)}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Remaining</span>
                  <span className="font-medium tabular-nums">
                    {subscription.washesIncluded === 'unlimited'
                      ? 'Unlimited'
                      : Math.max(0, Number(subscription.washesIncluded || 0) - Number(subscription.washesUsed || 0))}
                  </span>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <WashPriceListSection />

        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardList className="size-5" />
              Bay selections (from staff)
            </CardTitle>
            <CardDescription>
              When you check in, staff confirm your wash type and vehicle class. It appears
              here for your records.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {log.length === 0 ? (
              <p className="text-sm text-muted-foreground">No logged selections yet.</p>
            ) : (
              <ul className="space-y-3">
                {log.slice(0, 15).map((entry, idx) => (
                  <li
                    key={`${entry.washId}-${idx}`}
                    className="flex flex-wrap justify-between gap-2 border-b border-border pb-3 text-sm last:border-0"
                  >
                    <div>
                      <p className="font-medium text-foreground">
                        {entry.label ||
                          `${entry.serviceName || ''} - ${entry.tierLabel || ''}`}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {entry.at
                          ? new Date(entry.at).toLocaleString()
                          : ''}
                      </p>
                    </div>
                    <span className="tabular-nums font-medium">
                      {typeof entry.amount === 'number'
                        ? `R${entry.amount.toFixed(2)}`
                        : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Preferred packages</CardTitle>
            <CardDescription>Optional. Helps staff know what you usually want.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {packageNames.map((name) => (
                <label
                  key={name}
                  className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm cursor-pointer hover:bg-muted/40"
                >
                  <input
                    type="checkbox"
                    className="rounded border-input"
                    disabled={!editing}
                    checked={formData.preferredServices.includes(name)}
                    onChange={() => togglePreferredService(name)}
                  />
                  {name}
                </label>
              ))}
            </div>
            {!editing && (
              <p className="text-xs text-muted-foreground mt-3">
                Tap Edit on your contact card to change preferences.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
      <BottomNav />
    </div>
  )
}
